import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject, Subject, of } from 'rxjs';
import { tap, withLatestFrom, map, filter, shareReplay, startWith, take } from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckbox, MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

import { TranslationKey, TranslationModule, TranslationService } from '@lib/translation';
import { SnIconModule } from '@lib/ui/icon';

import {
  IFsLprProviderInterface,
  LPRResultsCameraVendor,
  LPRResultsCameraModel,
} from '../../../../../api';
import { ViLPRProvider } from '../../../../../shared/models';
import { AxonViLPRCameraModelMapperPipe } from '../../../../../shared/pipes';
import { calculateCameraModelSelectionState } from '../../helpers/camera-model-selection/lpr-camera-model-selection-helper';
import {
  calculateCameraModelExclusions,
  calculateProviderCount,
  calculateWorksWithAxonCount,
} from '../../helpers/provider-count-calculation/lpr-provider-count-calculator';
import {
  LPRCameraModelCheckboxChange,
  LPRCameraModelSelection,
  LPRCameraModelsSelection,
  LPRProviderSelection,
} from '../../models';

@UntilDestroy()
@Component({
  selector: 'fs-lpr-source-dropdown-entry',
  standalone: true,
  templateUrl: './lpr-source-dropdown-entry.component.html',
  styleUrls: ['./lpr-source-dropdown-entry.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TranslationModule,
    AxonViLPRCameraModelMapperPipe,
    SnIconModule,
  ],
})
export class LPRSourceDropdownEntryComponent implements OnInit {
  @Input() isWorksWithAxon = false;
  @Input() providerNameTranslationKey: string;
  @Input() provider$: Observable<IFsLprProviderInterface>;
  @Input() isProviderLoaded$: Observable<boolean>;
  @Input() count$: Observable<number> = of(0);
  @Input() cameraVendor$: Observable<LPRResultsCameraVendor | undefined> = of(undefined);
  @Input() cameraModels$: Observable<LPRResultsCameraModel[]>;
  @Input() shouldShowPartialMatches$: Observable<boolean>;
  readonly worksWithAxonCheckboxName = ViLPRProvider.WORKS_WITH_AXON;
  hasProviderErrors$: Observable<boolean>;
  providerWarningMessages$: Observable<string>;
  providerErrorMessages$: Observable<string>;
  hasProviderWarnings$: Observable<boolean>;
  providerCount$: Observable<number>;
  @ViewChild('providerCheckbox', { static: true }) providerCheckbox: MatCheckbox;
  providerCheckbox$ = new BehaviorSubject<boolean>(true);
  private providerSelection$ = new Subject<LPRProviderSelection>();
  @Output() providerSelectionChange: Observable<LPRProviderSelection> = this.providerSelection$;
  cameraModelCheckbox$ = new Subject<LPRCameraModelCheckboxChange>();
  cameraModelSelection$ = new ReplaySubject<LPRCameraModelsSelection>(1);
  @Output() cameraModelSelectionChange: Observable<LPRCameraModelsSelection> =
    this.cameraModelSelection$;

  // Prevents infinite loops when syncing provider ↔ camera model checkboxes
  private isUpdatingProviderFromCameraModels = false;
  private isTogglingAllCameraModels = false;

  constructor(private readonly translationService: TranslationService) {}

  ngOnInit(): void {
    this.setHasProviderErrorsSubscription();
    this.setHasProviderWarningsSubscription();
    this.setProviderWarningsSubscription();
    this.setProviderErrorMessagesSubscription();
    this.setProviderCountSubscription();
    if (this.cameraModels$) {
      this.setCameraModelCheckboxSubscription();
      this.setCameraModelsSelectionStateSubscription();
    }
    this.setProviderCheckboxSubscription(); // Must be last to avoid race conditions
  }

  /*
   * Syncs provider checkbox with camera models: checking provider checks all models,
   * unchecking provider unchecks all models
   */
  private setProviderCheckboxSubscription(): void {
    this.providerCheckbox$
      .pipe(
        untilDestroyed(this),
        withLatestFrom(this.provider$),
        tap(([isSelected, provider]) => {
          this.emitProviderSelectionChange(provider, isSelected);
          if (this.providerCheckbox) {
            this.providerCheckbox.checked = isSelected;
          }
          // Guard: prevent infinite loop when this change originated from camera models
          if (!this.cameraModels$ || this.isUpdatingProviderFromCameraModels) {
            this.isUpdatingProviderFromCameraModels = false;
            return;
          }
          this.isTogglingAllCameraModels = true;
          this.toggleAllCameraModels(isSelected);
        }),
      )
      .subscribe();
  }

  private setCameraModelCheckboxSubscription(): void {
    if (!this.cameraModels$) return;
    this.cameraModelCheckbox$
      .pipe(
        untilDestroyed(this),
        withLatestFrom(
          this.cameraModelSelection$.pipe(
            startWith({
              providerName: undefined,
              cameraVendor: undefined,
              cameraModels: new Map<string, boolean>(),
            } as LPRCameraModelsSelection),
          ),
          this.cameraVendor$,
          this.provider$,
        ),
        tap(([checkboxChange, currentSelection, cameraVendor, provider]) => {
          const selection: LPRCameraModelsSelection = {
            providerName: this.isWorksWithAxon
              ? ViLPRProvider.WORKS_WITH_AXON
              : (provider.nameEnum),
            cameraVendor: cameraVendor?.vendor,
            cameraModels: new Map(currentSelection.cameraModels),
          };
          selection.cameraModels.set(checkboxChange.name, checkboxChange.isSelected);
          this.cameraModelSelection$.next(selection);
        }),
      )
      .subscribe();
  }

  /*
   * Syncs provider checkbox based on camera model state:
   * - All unchecked → uncheck provider
   * - Any checked → check provider
   */
  private setCameraModelsSelectionStateSubscription(): void {
    if (!this.cameraModels$) {
      return;
    }

    this.cameraModelSelection$
      .pipe(
        untilDestroyed(this),
        map((selection) => calculateCameraModelSelectionState(selection)),
        withLatestFrom(this.provider$),
        tap(([state, provider]) => {
          if (!this.providerCheckbox) return;
          // Guard: prevent infinite loop when this change originated from provider checkbox
          if (this.isTogglingAllCameraModels) {
            this.isTogglingAllCameraModels = false;
            return;
          }
          const shouldProviderBeChecked = state.anySelected;
          const isProviderChecked = this.providerCheckbox.checked;
          if (isProviderChecked === shouldProviderBeChecked) return;
          this.isUpdatingProviderFromCameraModels = true;
          this.providerCheckbox.checked = shouldProviderBeChecked;
          this.providerCheckbox$.next(shouldProviderBeChecked);
        }),
      )
      .subscribe();
  }

  private setHasProviderErrorsSubscription(): void {
    this.hasProviderErrors$ = this.isProviderLoaded$.pipe(
      filter((isLoaded) => isLoaded),
      withLatestFrom(this.provider$),
      map(([_, provider]) => !!(provider.errors && provider.errors.length > 0)),
      shareReplay(1),
    );
  }

  private setHasProviderWarningsSubscription(): void {
    this.hasProviderWarnings$ = this.isProviderLoaded$.pipe(
      filter((isLoaded) => isLoaded),
      withLatestFrom(this.provider$, this.hasProviderErrors$),
      map(
        ([_, provider, hasProviderErrors]) =>
          !!(provider.warnings && provider.warnings.length > 0) && !hasProviderErrors,
      ),
      shareReplay(1),
    );
  }

  private setProviderWarningsSubscription(): void {
    this.providerWarningMessages$ = this.provider$.pipe(
      map((provider) =>
        (provider.warnings || [])
          .map((w) => this.translationService.translate(w.message as unknown as TranslationKey))
          .join('\n\n')
      ),
    );
  }

  private setProviderErrorMessagesSubscription(): void {
    this.providerErrorMessages$ = this.provider$.pipe(
      map((provider) =>
        (provider.errors || [])
          .map((error) => this.translationService.translate(error as unknown as TranslationKey))
          .join('\n\n')
      ),
    );
  }

  /*
   * Count calculation steps:
   * 1. Return 0 if provider isn't loaded or has issues (when partial matches hidden)
   * 2. Calculate base count using provider-specific logic (WWA vs standard providers)
   * 3. Subtract counts from unselected camera models (Axon VI and WWA only)
   * 4. Clamp to 0 minimum
   */
  private setProviderCountSubscription(): void {
    const cameraModels$: Observable<LPRResultsCameraModel[]> = this.cameraModels$ || of([]);

    this.providerCount$ = combineLatest([
      this.provider$,
      this.isProviderLoaded$,
      this.hasProviderWarnings$,
      this.hasProviderErrors$,
      this.cameraVendor$,
      this.count$,
      cameraModels$,
      this.shouldShowPartialMatches$,
      this.cameraModelSelection$.pipe(startWith(undefined)),
    ] as [
      Observable<IFsLprProviderInterface>,
      Observable<boolean>,
      Observable<boolean>,
      Observable<boolean>,
      Observable<LPRResultsCameraVendor | undefined>,
      Observable<number>,
      Observable<LPRResultsCameraModel[]>,
      Observable<boolean>,
      Observable<LPRCameraModelsSelection | undefined>,
    ]).pipe(
      map(
        ([
          provider,
          isLoaded,
          hasWarnings,
          hasErrors,
          cameraVendor,
          providedCount,
          cameraModels,
          shouldShowPartialMatches,
          cameraModelSelection,
        ]: [
          IFsLprProviderInterface,
          boolean,
          boolean,
          boolean,
          LPRResultsCameraVendor | undefined,
          number,
          LPRResultsCameraModel[],
          boolean,
          LPRCameraModelsSelection | undefined,
        ]) => {
          if (!isLoaded) return 0;
          const shouldShowCount = shouldShowPartialMatches || (!hasWarnings && !hasErrors);
          if (!shouldShowCount) return 0;
          const baseCount = this.isWorksWithAxon
            ? calculateWorksWithAxonCount(providedCount, cameraModels)
            : calculateProviderCount(provider, cameraVendor, providedCount, cameraModels);
          const exclusionCount = calculateCameraModelExclusions(
            provider,
            cameraModels,
            cameraModelSelection,
            this.isWorksWithAxon,
          );
          return Math.max(0, baseCount - exclusionCount);
        },
      ),
      shareReplay(1),
    );
  }

  private toggleAllCameraModels(isSelected: boolean): void {
    if (!this.cameraModels$) return;
    combineLatest([this.cameraModels$, this.cameraVendor$, this.provider$])
      .pipe(
        untilDestroyed(this),
        filter(([models]) => models.length > 0),
        tap(([cameraModels, cameraVendor, provider]) => {
          const selection: LPRCameraModelSelection = new Map<string, boolean>();
          cameraModels.forEach((model) => selection.set(model.model, isSelected));
          this.cameraModelSelection$.next({
            providerName: this.isWorksWithAxon
              ? ViLPRProvider.WORKS_WITH_AXON
              : (provider.nameEnum),
            cameraVendor: cameraVendor?.vendor,
            cameraModels: selection,
          });
        }),
        take(1),
      )
      .subscribe();
  }

  private emitProviderSelectionChange(
    provider: IFsLprProviderInterface,
    isSelected: boolean,
  ): void {
    const providerSelection: LPRProviderSelection = {
      name: this.isWorksWithAxon ? ViLPRProvider.WORKS_WITH_AXON : (provider.nameEnum),
      isSelected,
    };

    this.providerSelection$.next(providerSelection);
  }
}
